// Inline SVG icon set — hand-drawn 24×24 stroke glyphs, no icon
// dependency. stroke=currentColor so an icon inherits the text color of
// its surroundings (tab active tint, destructive rows). Size comes from
// the .icon class (styles/icons.css) or the host box, not attributes.

export type IconName =
  | 'home'
  | 'capture'
  | 'cases'
  | 'me'
  | 'chevron-right'
  | 'devices'
  | 'appearance'
  | 'sign-out'
  | 'passkey'
  | 'reports'
  | 'lock'
  | 'compass'
  | 'cart'
  | 'box'
  | 'settings'
  | 'alert';

const PATHS: Record<IconName, string[]> = {
  home: ['M3 10.75 12 3l9 7.75', 'M5.5 9.5V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5'],
  capture: ['M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'],
  cases: [
    'M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z',
  ],
  me: ['M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z'],
  'chevron-right': ['m9 18 6-6-6-6'],
  devices: ['M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z', 'M12 18h.01'],
  appearance: ['M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z'],
  'sign-out': ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'm16 17 5-5-5-5', 'M21 12H9'],
  passkey: ['M7.5 10a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z', 'm21 2-9.6 9.6', 'm15.5 7.5 3 3L22 7l-3-3'],
  reports: ['M3 3v18h18', 'M8 17v-4', 'M13 17V8', 'M18 17v-7'],
  lock: ['M8 11V7a4 4 0 1 1 8 0v4', 'M6.5 11h11a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z', 'M12 15v2'],
  compass: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'm15 9-2 4-4 2 2-4 4-2z'],
  cart: ['M6 8h12l-1 12a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1L6 8z', 'M9 8V6a3 3 0 0 1 6 0v2'],
  box: ['M3 7l9-4 9 4-9 4-9-4z', 'M3 7v10l9 4 9-4V7', 'M12 11v10'],
  settings: [
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M19.4 13a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.2a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-2.8-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.3 6l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V2a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V8a1.6 1.6 0 0 0 1.5 1H22a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z',
  ],
  alert: ['M12 4 3 19h18L12 4z', 'M12 10v4', 'M12 17h.01'],
};

const SVG_NS = 'http://www.w3.org/2000/svg';

export function icon(name: IconName): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.8');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.classList.add('icon');
  for (const d of PATHS[name]) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
  }
  return svg;
}
